import { useState, useEffect, useRef, useCallback } from 'react';
import PDFCalibrationGrid from './PDFCalibrationGrid';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * AttestationPDFCalibrationTool
 * Outil de calibration jumeau de PDFCalibrationTool, mais pour le rapport
 * Attestation (US Letter 612 × 792 pts, généré nativement via PyMuPDF).
 * Endpoints : /api/pdf-attest-calibration/*
 */
export default function AttestationPDFCalibrationTool({ onClose }) {
  const [fields, setFields] = useState([]);
  const [boxes, setBoxes] = useState([]);
  const [lines, setLines] = useState([]);
  const [offsetY, setOffsetY] = useState(0);
  const [selectedField, setSelectedField] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [showPreview, setShowPreview] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [filterText, setFilterText] = useState('');
  const [showGrid, setShowGrid] = useState(true);
  const [snapToGrid, setSnapToGrid] = useState(true);
  const GRID_STEP = 10;

  const containerRef = useRef(null);
  const imageRef = useRef(null);

  // Dimensions du PDF Attestation (612 x 792 pts, US Letter, image 2x)
  const PDF_WIDTH = 612;
  const PDF_HEIGHT = 792;
  const SCALE = 2;

  useEffect(() => { loadFields(); }, []);

  const loadFields = async () => {
    try {
      const res = await fetch(`${API}/pdf-attest-calibration/fields`);
      const data = await res.json();
      setFields(data.fields || []);
      setBoxes(data.boxes || []);
      setLines(data.lines || []);
      setOffsetY(data.offset_y || 0);
    } catch (e) {
      console.error('Erreur chargement champs Attestation:', e);
    }
  };

  const saveFields = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/pdf-attest-calibration/fields`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ offset_y: offsetY, fields, boxes, lines })
      });
      await res.json();
      setMessage({ type: 'success', text: 'Positions Attestation sauvegardées!' });
      setTimeout(() => setMessage(null), 3000);
    } catch (e) {
      setMessage({ type: 'error', text: 'Erreur de sauvegarde' });
    }
    setSaving(false);
  };

  const handleMouseDown = (e, field) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedField(field);
    setIsDragging(true);
    setDragStart({
      mouseX: e.clientX,
      mouseY: e.clientY,
      fieldX: field.x,
      fieldY: field.y
    });
  };

  const handleMouseMove = useCallback((e) => {
    if (!isDragging || !selectedField || dragStart.fieldX === undefined) return;
    const dx = (e.clientX - dragStart.mouseX) / (SCALE * zoom);
    const dy = (e.clientY - dragStart.mouseY) / (SCALE * zoom);
    let newX = dragStart.fieldX + dx;
    let newY = dragStart.fieldY + dy;
    if (snapToGrid) {
      newX = Math.round(newX / GRID_STEP) * GRID_STEP;
      newY = Math.round(newY / GRID_STEP) * GRID_STEP;
    } else {
      newX = Math.round(newX * 10) / 10;
      newY = Math.round(newY * 10) / 10;
    }
    setFields(prev => prev.map(f =>
      f.id === selectedField.id ? { ...f, x: newX, y: newY } : f
    ));
  }, [isDragging, selectedField, dragStart, zoom, snapToGrid]);

  const handleMouseUp = () => setIsDragging(false);

  useEffect(() => {
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [handleMouseMove]);

  const updateFieldPosition = (fieldId, key, value) => {
    setFields(prev => prev.map(f =>
      f.id === fieldId ? { ...f, [key]: parseFloat(value) || 0 } : f
    ));
  };

  const toggleFieldWrap = (fieldId, wrapValue) => {
    setFields(prev => prev.map(f => {
      if (f.id !== fieldId) return f;
      const updated = { ...f, wrap: wrapValue };
      // Si on active wrap et pas de w/h, on met des valeurs par défaut
      if (wrapValue) {
        if (!updated.w) updated.w = 200;
        if (!updated.h) updated.h = 30;
      }
      return updated;
    }));
  };

  // Redimensionnement via le handle bas-droit du rectangle
  const [resizingField, setResizingField] = useState(null);
  const [resizeStart, setResizeStart] = useState({});
  const handleResizeMouseDown = (e, field) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedField(field);
    setResizingField(field);
    setResizeStart({
      mouseX: e.clientX,
      mouseY: e.clientY,
      w: field.w || 100,
      h: field.h || 20,
    });
  };
  const handleResizeMouseMove = useCallback((e) => {
    if (!resizingField || resizeStart.mouseX === undefined) return;
    const dx = (e.clientX - resizeStart.mouseX) / (SCALE * zoom);
    const dy = (e.clientY - resizeStart.mouseY) / (SCALE * zoom);
    let newW = resizeStart.w + dx;
    let newH = resizeStart.h + dy;
    if (snapToGrid) {
      newW = Math.max(GRID_STEP, Math.round(newW / GRID_STEP) * GRID_STEP);
      newH = Math.max(GRID_STEP, Math.round(newH / GRID_STEP) * GRID_STEP);
    } else {
      newW = Math.max(10, Math.round(newW * 10) / 10);
      newH = Math.max(10, Math.round(newH * 10) / 10);
    }
    setFields(prev => prev.map(f =>
      f.id === resizingField.id ? { ...f, w: newW, h: newH } : f
    ));
  }, [resizingField, resizeStart, zoom, snapToGrid]);
  useEffect(() => {
    const up = () => setResizingField(null);
    window.addEventListener('mousemove', handleResizeMouseMove);
    window.addEventListener('mouseup', up);
    return () => {
      window.removeEventListener('mousemove', handleResizeMouseMove);
      window.removeEventListener('mouseup', up);
    };
  }, [handleResizeMouseMove]);

  const filteredFields = fields.filter(f =>
    filterText === '' || f.label.toLowerCase().includes(filterText.toLowerCase())
    || f.id.toLowerCase().includes(filterText.toLowerCase())
  );

  const getFieldStyle = (field) => {
    const x = field.x * SCALE * zoom;
    const y = (field.y + offsetY) * SCALE * zoom;
    const w = (field.w || 80) * SCALE * zoom;
    const isWrap = !!field.wrap;
    const singleH = (field.fs || 10) * SCALE * zoom + 4 * zoom;
    const h = isWrap ? (field.h || 30) * SCALE * zoom : singleH;
    const isCheckbox = field.kind === 'checkbox';
    const isStatic = field.kind === 'static' || field.kind === 'textbox';
    const isSelected = selectedField?.id === field.id;

    let borderColor, bgColor;
    if (isSelected) {
      borderColor = '#ff0000';
      bgColor = 'rgba(255,0,0,0.3)';
    } else if (isCheckbox) {
      borderColor = '#22c55e';
      bgColor = 'rgba(34,197,94,0.2)';
    } else if (isStatic) {
      borderColor = '#a78bfa';           // Violet = libellé statique
      bgColor = 'rgba(167,139,250,0.15)';
    } else {
      borderColor = '#3b82f6';           // Bleu = donnée
      bgColor = 'rgba(59,130,246,0.15)';
    }

    return {
      position: 'absolute',
      left: x,
      top: isWrap ? y - singleH + 4 * zoom : (y - h + 4 * zoom),
      width: isCheckbox ? 14 * SCALE * zoom : w,
      height: isCheckbox ? 14 * SCALE * zoom : h,
      border: `2px solid ${borderColor}`,
      backgroundColor: bgColor,
      cursor: 'move',
      fontSize: Math.max(6, 7 * zoom),
      color: '#000',
      fontWeight: 'bold',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      overflow: 'hidden',
      whiteSpace: 'nowrap',
      textOverflow: 'ellipsis',
      userSelect: 'none',
      zIndex: isSelected ? 100 : 1,
      textShadow: '0 0 2px #fff, 0 0 2px #fff, 0 0 3px #fff'
    };
  };

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: '#1a1a2e', zIndex: 10000,
      display: 'flex', flexDirection: 'column'
    }} data-testid="attest-calibration-tool">
      {/* Header */}
      <div style={{
        backgroundColor: '#16213e', padding: '10px 20px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        borderBottom: '2px solid #0f3460'
      }}>
        <h2 style={{ color: '#e94560', margin: 0, fontSize: 18 }}>
          Calibration PDF Attestation — Positionnement des champs
        </h2>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <label style={{ color: '#fff', fontSize: 13 }}>
            Décalage Y global:
            <input type="number" value={offsetY}
              onChange={(e) => setOffsetY(parseFloat(e.target.value) || 0)}
              step="0.5"
              style={{ width: 60, marginLeft: 8, padding: '4px 8px', borderRadius: 4, border: 'none' }} />
          </label>
          <label style={{ color: '#fff', fontSize: 13 }}>
            Zoom:
            <select value={zoom} onChange={(e) => setZoom(parseFloat(e.target.value))}
              style={{ marginLeft: 8, padding: '4px 8px', borderRadius: 4, border: 'none' }}>
              <option value="0.5">50%</option>
              <option value="0.75">75%</option>
              <option value="1">100%</option>
              <option value="1.5">150%</option>
              <option value="2">200%</option>
            </select>
          </label>
          <button onClick={() => setShowGrid(v => !v)}
            data-testid="btn-attest-cal-grid"
            title="Afficher/masquer la grille (10 pt fin, 50 pt épais)"
            style={{
              padding: '8px 12px',
              backgroundColor: showGrid ? '#4caf50' : '#64748b',
              color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer'
            }}>
            Grille {showGrid ? 'ON' : 'OFF'}
          </button>
          <button onClick={() => setSnapToGrid(v => !v)}
            data-testid="btn-attest-cal-snap"
            title="Aligner les champs sur la grille (10 pt) lors du glisser"
            style={{
              padding: '8px 12px',
              backgroundColor: snapToGrid ? '#4caf50' : '#64748b',
              color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer'
            }}>
            Snap {snapToGrid ? 'ON' : 'OFF'}
          </button>
          <button onClick={() => setShowPreview(!showPreview)}
            data-testid="btn-attest-cal-preview"
            style={{
              padding: '8px 16px',
              backgroundColor: showPreview ? '#4caf50' : '#2196f3',
              color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer'
            }}>
            {showPreview ? 'Masquer aperçu' : 'Voir aperçu'}
          </button>
          <button onClick={loadFields}
            data-testid="btn-attest-cal-reload"
            style={{
              padding: '8px 16px', backgroundColor: '#ff9800',
              color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer'
            }}>
            Recharger
          </button>
          <button onClick={saveFields} disabled={saving}
            data-testid="btn-attest-cal-save"
            style={{
              padding: '8px 16px', backgroundColor: '#4caf50',
              color: '#fff', border: 'none', borderRadius: 4,
              cursor: saving ? 'wait' : 'pointer', opacity: saving ? 0.7 : 1
            }}>
            {saving ? 'Sauvegarde...' : 'Sauvegarder'}
          </button>
          <button onClick={onClose}
            data-testid="btn-attest-cal-close"
            style={{
              padding: '8px 16px', backgroundColor: '#e94560',
              color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer'
            }}>
            Fermer
          </button>
        </div>
      </div>

      {message && (
        <div style={{
          padding: '10px 20px',
          backgroundColor: message.type === 'success' ? '#4caf50' : '#f44336',
          color: '#fff', textAlign: 'center'
        }}>
          {message.text}
        </div>
      )}

      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* Panel gauche - Liste des champs */}
        <div style={{
          width: 320, backgroundColor: '#1f1f3d',
          borderRight: '2px solid #0f3460',
          display: 'flex', flexDirection: 'column', overflow: 'hidden'
        }}>
          <div style={{ padding: 10, borderBottom: '1px solid #0f3460' }}>
            <input type="text" placeholder="Filtrer les champs..."
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
              data-testid="attest-cal-filter"
              style={{
                width: '100%', padding: '8px 12px', borderRadius: 4,
                border: 'none', backgroundColor: '#16213e', color: '#fff'
              }} />
          </div>
          <div style={{ padding: '6px 12px', color: '#94a3b8', fontSize: 11,
                        borderBottom: '1px solid #0f3460', display: 'flex', gap: 10 }}>
            <span><span style={{ color: '#a78bfa' }}>●</span> Statique</span>
            <span><span style={{ color: '#3b82f6' }}>●</span> Donnée</span>
            <span><span style={{ color: '#22c55e' }}>●</span> Case</span>
          </div>
          <div style={{ flex: 1, overflowY: 'auto', padding: 10 }}>
            {filteredFields.map((field) => {
              const isOutOfBounds = field.x < 0 || field.x > PDF_WIDTH
                                  || field.y < 0 || field.y > PDF_HEIGHT;
              const isCheckbox = field.kind === 'checkbox';
              const isStatic = field.kind === 'static' || field.kind === 'textbox';
              const isSelected = selectedField?.id === field.id;

              let bgColor, borderColor;
              if (isSelected)        { bgColor = '#dc2626'; borderColor = '#dc2626'; }
              else if (isOutOfBounds){ bgColor = '#7f1d1d'; borderColor = '#ef4444'; }
              else if (isCheckbox)   { bgColor = '#14532d'; borderColor = '#22c55e'; }
              else if (isStatic)     { bgColor = '#2e1065'; borderColor = '#a78bfa'; }
              else                   { bgColor = '#16213e'; borderColor = 'transparent'; }

              return (
                <div key={field.id} onClick={() => setSelectedField(field)}
                  data-testid={`attest-cal-fld-${field.id}`}
                  style={{
                    padding: '8px 10px', marginBottom: 4,
                    backgroundColor: bgColor, borderRadius: 4,
                    cursor: 'pointer', border: `1px solid ${borderColor}`
                  }}>
                  <div style={{ color: '#fff', fontSize: 12, fontWeight: 'bold',
                                display: 'flex', justifyContent: 'space-between' }}>
                    <span>{field.label}</span>
                    {isOutOfBounds && <span style={{ color: '#ff6b6b' }}>⚠️</span>}
                  </div>
                  {field.desc && (
                    <div style={{ color: '#9ca3af', fontSize: 11, marginTop: 2 }}>
                      → {field.desc}
                    </div>
                  )}
                  <div style={{ color: '#6b7280', fontSize: 10, marginTop: 2 }}>
                    X: {field.x} | Y: {field.y} {field.w ? `| W: ${field.w}` : ''}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Zone centrale - PDF avec champs */}
        <div ref={containerRef}
          style={{
            flex: 1, overflow: 'auto', backgroundColor: '#2d2d44',
            display: 'flex', justifyContent: 'center', padding: 20
          }}>
          <div style={{ position: 'relative', display: 'inline-block' }}>
            <img ref={imageRef}
              src={showPreview
                ? `${API}/pdf-attest-calibration/preview?t=${Date.now()}`
                : `${API}/pdf-attest-calibration/template-image?t=${Date.now()}`}
              alt="Attestation Template"
              style={{
                width: PDF_WIDTH * SCALE * zoom,
                height: PDF_HEIGHT * SCALE * zoom,
                display: 'block'
              }}
              draggable={false} />
            {showGrid && !showPreview && (
              <PDFCalibrationGrid
                pdfWidth={PDF_WIDTH} pdfHeight={PDF_HEIGHT}
                scale={SCALE} zoom={zoom} step={GRID_STEP} majorStep={50} />
            )}
            {!showPreview && fields.map((field) => (
              <div key={field.id}
                style={getFieldStyle(field)}
                onMouseDown={(e) => handleMouseDown(e, field)}
                title={`${field.label}\nX: ${field.x}, Y: ${field.y}`}>
                <span style={{
                  fontSize: Math.max(5, 6 * zoom), padding: '0 2px',
                  maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis'
                }}>
                  {field.label}
                </span>
                {field.wrap && (
                  <div
                    data-testid={`attest-cal-resize-${field.id}`}
                    onMouseDown={(e) => handleResizeMouseDown(e, field)}
                    style={{
                      position: 'absolute',
                      right: -3, bottom: -3,
                      width: 10, height: 10,
                      backgroundColor: '#f59e0b',
                      border: '1px solid #000',
                      cursor: 'nwse-resize',
                      zIndex: 200,
                    }}
                    title="Redimensionner (w × h)"
                  />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Panel droit - Détails du champ sélectionné */}
        {selectedField && (
          <div style={{
            width: 280, backgroundColor: '#1f1f3d',
            borderLeft: '2px solid #0f3460', padding: 15, overflowY: 'auto'
          }}>
            <h3 style={{ color: '#e94560', margin: '0 0 15px 0', fontSize: 14 }}>
              Champ sélectionné <span style={{ color: '#22c55e', fontSize: 10, fontWeight: 400 }}>v2</span>
            </h3>
            <div style={{ color: '#fff', marginBottom: 15 }}>
              <strong style={{ fontSize: 14 }}>{selectedField.label}</strong>
              {selectedField.desc && (
                <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 4 }}>
                  → {selectedField.desc}
                </div>
              )}
              <div style={{ fontSize: 10, color: '#6b7280', marginTop: 4 }}>
                ID: {selectedField.id} | kind: {selectedField.kind}
              </div>
            </div>

            {['x', 'y', 'fs'].map((k) => (
              <div key={k} style={{ marginBottom: 12 }}>
                <label style={{ color: '#aaa', fontSize: 11, display: 'block',
                                marginBottom: 4 }}>
                  {k === 'x' ? 'Position X'
                   : k === 'y' ? 'Position Y'
                   : 'Taille police (fs)'}
                </label>
                <input type="number"
                  value={selectedField[k] ?? (k === 'fs' ? 10 : 0)}
                  onChange={(e) => updateFieldPosition(selectedField.id, k, e.target.value)}
                  step="0.5"
                  data-testid={`attest-cal-input-${k}`}
                  style={{
                    width: '100%', padding: '8px 10px', borderRadius: 4,
                    border: 'none', backgroundColor: '#16213e', color: '#fff'
                  }} />
              </div>
            ))}
            {selectedField.w !== undefined && (
              <div style={{ marginBottom: 12 }}>
                <label style={{ color: '#aaa', fontSize: 11, display: 'block',
                                marginBottom: 4 }}>Largeur (w)</label>
                <input type="number" value={selectedField.w}
                  onChange={(e) => updateFieldPosition(selectedField.id, 'w', e.target.value)}
                  step="1"
                  style={{
                    width: '100%', padding: '8px 10px', borderRadius: 4,
                    border: 'none', backgroundColor: '#16213e', color: '#fff'
                  }} />
              </div>
            )}

            {/* Wrap / retour à la ligne automatique — data & textbox uniquement */}
            {selectedField.kind !== 'checkbox' && selectedField.kind !== 'static' && (
              <div style={{ marginBottom: 12, padding: 10,
                            backgroundColor: '#14532d1a', borderRadius: 4,
                            border: '1px solid #22c55e' }}>
                <label style={{ color: '#22c55e', fontSize: 12,
                                display: 'flex', alignItems: 'center', gap: 8,
                                cursor: 'pointer' }}>
                  <input type="checkbox"
                    data-testid="attest-cal-input-wrap"
                    checked={!!selectedField.wrap}
                    onChange={(e) => toggleFieldWrap(selectedField.id, e.target.checked)}
                    style={{ width: 16, height: 16, cursor: 'pointer' }} />
                  <strong>Retour à la ligne automatique</strong>
                </label>
                {selectedField.wrap && (
                  <div style={{ marginTop: 10 }}>
                    <label style={{ color: '#aaa', fontSize: 11, display: 'block',
                                    marginBottom: 4 }}>
                      Hauteur initiale (h) — s&apos;étend si nécessaire
                    </label>
                    <input type="number"
                      value={selectedField.h ?? 30}
                      onChange={(e) => updateFieldPosition(selectedField.id, 'h', e.target.value)}
                      step="1"
                      data-testid="attest-cal-input-h"
                      style={{
                        width: '100%', padding: '8px 10px', borderRadius: 4,
                        border: 'none', backgroundColor: '#16213e', color: '#fff'
                      }} />
                    <label style={{ color: '#aaa', fontSize: 11, display: 'block',
                                    marginTop: 10, marginBottom: 4 }}>
                      Alignement du texte
                    </label>
                    <div style={{ display: 'flex', gap: 4 }}>
                      {[
                        {v: 'left',   sym: '⇤',  label: 'Gauche'},
                        {v: 'center', sym: '⇔',  label: 'Centre'},
                        {v: 'right',  sym: '⇥',  label: 'Droite'},
                      ].map(a => {
                        const active = (selectedField.align || 'left') === a.v;
                        return (
                          <button key={a.v}
                            data-testid={`attest-cal-align-${a.v}`}
                            onClick={() => setFields(prev => prev.map(f =>
                              f.id === selectedField.id ? { ...f, align: a.v } : f))}
                            title={a.label}
                            style={{
                              flex: 1, padding: '6px 4px',
                              backgroundColor: active ? '#22c55e' : '#16213e',
                              color: '#fff', border: `1px solid ${active ? '#22c55e' : '#334155'}`,
                              borderRadius: 4, cursor: 'pointer', fontSize: 16,
                              fontWeight: active ? 700 : 400,
                            }}>
                            {a.sym}
                          </button>
                        );
                      })}
                    </div>
                    <label style={{ color: '#aaa', fontSize: 11, display: 'block',
                                    marginTop: 10, marginBottom: 4 }}>
                      Alignement vertical
                    </label>
                    <div style={{ display: 'flex', gap: 4 }}>
                      {[
                        {v: 'top',    sym: '⤒',  label: 'Haut'},
                        {v: 'middle', sym: '↕',  label: 'Milieu'},
                        {v: 'bottom', sym: '⤓',  label: 'Bas'},
                      ].map(a => {
                        const active = (selectedField.valign || 'top') === a.v;
                        return (
                          <button key={a.v}
                            data-testid={`attest-cal-valign-${a.v}`}
                            onClick={() => setFields(prev => prev.map(f =>
                              f.id === selectedField.id ? { ...f, valign: a.v } : f))}
                            title={a.label}
                            style={{
                              flex: 1, padding: '6px 4px',
                              backgroundColor: active ? '#22c55e' : '#16213e',
                              color: '#fff', border: `1px solid ${active ? '#22c55e' : '#334155'}`,
                              borderRadius: 4, cursor: 'pointer', fontSize: 16,
                              fontWeight: active ? 700 : 400,
                            }}>
                            {a.sym}
                          </button>
                        );
                      })}
                    </div>
                    <label style={{ color: '#aaa', fontSize: 11, display: 'block',
                                    marginTop: 10, marginBottom: 4 }}>
                      Interligne (line_height) — défaut fs × 1.25
                    </label>
                    <input type="number"
                      value={selectedField.line_height ?? Number(((selectedField.fs || 10) * 1.25).toFixed(1))}
                      onChange={(e) => updateFieldPosition(selectedField.id, 'line_height', e.target.value)}
                      step="0.5" min="4"
                      data-testid="attest-cal-input-lh"
                      style={{
                        width: '100%', padding: '8px 10px', borderRadius: 4,
                        border: 'none', backgroundColor: '#16213e', color: '#fff'
                      }} />
                    <div style={{ color: '#94a3b8', fontSize: 10, marginTop: 6,
                                  fontStyle: 'italic' }}>
                      Astuce : tirez la poignée orange en bas-droite du rectangle
                      pour redimensionner visuellement.
                    </div>
                  </div>
                )}
              </div>
            )}

            <button
              onClick={() => {
                updateFieldPosition(selectedField.id, 'x', 200);
                updateFieldPosition(selectedField.id, 'y', 200);
                setMessage({ type: 'success',
                             text: `${selectedField.label} recentré à (200, 200)` });
                setTimeout(() => setMessage(null), 2000);
              }}
              style={{
                width: '100%', padding: '10px', backgroundColor: '#ff9800',
                color: '#fff', border: 'none', borderRadius: 4,
                cursor: 'pointer', fontWeight: 'bold', marginBottom: 10
              }}>
              Recentrer ce champ
            </button>

            <div style={{
              marginTop: 20, padding: 10, backgroundColor: '#16213e',
              borderRadius: 4, fontSize: 11, color: '#aaa'
            }}>
              <strong style={{ color: '#fff' }}>Raccourcis:</strong><br />
              • Cliquez et glissez pour déplacer<br />
              • Ajustez précisément via les champs numériques<br />
              • &quot;Voir aperçu&quot; génère le PDF avec données démo
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
